import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SEED_LESSONS } from "../data/seed-lessons";
import { useLocalProfile } from "../local/useLocalProfile";
import { AvatarPreview } from "../components/AvatarEditor";
import { MainMenuHero } from "../components/MainMenuHero";
import { isLessonFinished, isLessonUnlocked } from "../local/profile";

/**
 * Main menu. Standard game shape:
 *   ▶ Play        → level select
 *   ⚔ Battle      → multiplayer race lobby
 *   ⚙ Settings    → name / avatar / reset
 *
 * If no character exists yet, the only action is "Make character" → /create.
 */
export function LocalLanding() {
  const { profile } = useLocalProfile();
  const nav = useNavigate();
  const [modeOpen, setModeOpen] = useState(false);
  const lessons = [...SEED_LESSONS].sort((a, b) => a.orderIndex - b.orderIndex);

  // Force character creation before the menu shows.
  if (!profile) {
    return (
      <div className="shell-narrow stack-loose" style={{ paddingTop: "10vh" }}>
        <div className="t-center">
          <h1 className="t-display">Word Runner</h1>
          <p className="t-body t-muted">Run, dodge, and learn English.</p>
        </div>
        <div className="card stack">
          <button className="btn btn-primary btn-big btn-block" onClick={() => nav("/create")}>
            ▶ Make character
          </button>
        </div>
        <p className="t-caption t-muted t-center" style={{ marginTop: 32, textTransform: "none" }}>
          Pictures by{" "}
          <a href="https://openmoji.org" target="_blank" rel="noreferrer">
            OpenMoji
          </a>{" "}
          · Voice by Mistral AI
        </p>
      </div>
    );
  }

  const unlockedLessons = lessons.filter((lesson) => isLessonUnlocked(profile, lesson.orderIndex, lessons));
  const currentLesson =
    lessons.find(
      (lesson) =>
        isLessonUnlocked(profile, lesson.orderIndex, lessons) &&
        !isLessonFinished(profile, lesson.id),
    ) ??
    unlockedLessons.at(-1) ??
    lessons[0]!;
  const playerLevel = Math.max(1, currentLesson.orderIndex);
  const totalStars = lessons.reduce(
    (sum, lesson) => sum + lesson.words.filter((w) => profile.mastery[w.id]?.timesCorrect).length,
    0,
  );
  const xpGoal = 800;
  const xpNow = Math.min(xpGoal, (profile.coins * 8 + totalStars * 30) % xpGoal);
  const xpPct = Math.max(8, Math.round((xpNow / xpGoal) * 100));

  return (
    <div className="home-dashboard home-lobby">
      <div className="home-lobby-bar">
        <div className="home-lobby-title">Lobby</div>

        <div className="home-lobby-tabs" aria-label="Main menu">
          <button className="is-active" aria-label="Play">▶</button>
          <button onClick={() => nav("/settings")} aria-label="Look">◇</button>
          <button className="home-settings-btn" onClick={() => nav("/settings")} aria-label="Settings">
            ⚙
          </button>
        </div>

        <div className="home-resource-row">
          <div className="home-resource-pill home-resource-compact">
            <span className="home-resource-icon">★</span>
            <strong>{totalStars}</strong>
          </div>
          <div className="home-resource-pill home-resource-compact">
            <span className="home-resource-icon">●</span>
            <strong>{profile.coins}</strong>
          </div>
        </div>
      </div>

      <section className="home-hero-panel home-lobby-stage">
        <MainMenuHero
          avatar={profile.avatarConfig}
          theme={currentLesson.theme}
          timeOfDay={currentLesson.timeOfDay}
        />
        <div className="home-lobby-brand">
          <h1>Word Runner</h1>
          <p>Season 1</p>
        </div>

        <button className="home-player-card" onClick={() => nav("/settings")}>
          <span className="home-player-face">
            <AvatarPreview cfg={profile.avatarConfig} />
          </span>
          <span>
            <span className="home-player-name">{profile.displayName}</span>
            <span className="home-player-level">Level {playerLevel}</span>
          </span>
        </button>

        <div className="home-season-progress">
          <div className="home-season-title">Season Progress</div>
          <div className="home-season-track">
            <span className="home-season-badge">{currentLesson.orderIndex}</span>
            <i>
              <b style={{ width: `${xpPct}%` }} />
            </i>
            <strong>{xpNow}/{xpGoal}</strong>
          </div>
        </div>

        <button className="home-lobby-play" onClick={() => setModeOpen(true)}>
          <span>▶</span>
          Play!
        </button>
      </section>

      {modeOpen && (
        <div className="home-mode-overlay" role="dialog" aria-modal="true" aria-label="Choose mode">
          <div className="home-mode-card">
            <button className="home-mode-close" onClick={() => setModeOpen(false)} aria-label="Close">
              ×
            </button>
            <h2>Choose Mode</h2>
            <div className="home-mode-actions">
              <button className="home-mode-btn home-mode-solo" onClick={() => nav("/play")}>
                <span>▶</span>
                Solo Run
              </button>
              <button className="home-mode-btn home-mode-battle" onClick={() => nav("/battle")}>
                <span>⚔</span>
                Battle
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="home-credit">
        Pictures by{" "}
        <Link to="/settings">
          OpenMoji
        </Link>{" "}
        · Voice by Mistral AI
      </p>
    </div>
  );
}
